// Encrypted TTL cache for confidential client-side data (SEC-0.3).
//
// All confidential cache / queue data on the device is encrypted at rest with an AES-GCM key and
// carries a TTL so stale confidential data self-expires (bounding the stolen-device residual risk).
// `wipeLocalConfidential()` clears EVERYTHING this module owns, called on logout / failed biometric /
// remote-wipe push.
//
// Crypto: Web Crypto SubtleCrypto AES-256-GCM with a random 96-bit IV per value. The persisted
// envelope is `{ iv, ct, exp }` (base64 iv + ciphertext, epoch-ms expiry).
//
// Key management:
//  - The AES key is generated once (extractable) and persisted (as raw base64) under a dedicated
//    secure-store key, then reused. Generating it lazily keeps the website unaffected.
//  - HARD HARDENING NOTE (build-host): on NATIVE the AES key must live in the hardware-backed
//    Keychain (iOS) / Keystore (Android) via a secure-storage plugin (e.g.
//    @aparajita/capacitor-secure-storage). It is NOT installed here, so we fall back to
//    @capacitor/preferences (app-sandboxed UserDefaults / SharedPreferences) — sandboxed but not
//    hardware-encrypted. At hardening time, repoint `readSecure`/`writeSecure` at the Keychain
//    plugin; the envelope format and public API are unchanged by that swap. On WEB the key sits in
//    localStorage (best available; a determined local attacker with the device unlocked can read it
//    — the TTL + wipe-on-logout bound that exposure).
//
// Everything is async. Native calls are guarded by isNative() so the website behaves exactly as
// before (web → localStorage, identical API surface).

import { isNative } from '@/lib/native/platform';

// Namespace so wipeLocalConfidential() can enumerate + clear exactly this module's keys.
const NS = 'cbam.conf.';
const KEY_NAME = `${NS}__aeskey__`;

interface Envelope {
  iv: string; // base64 (12 bytes)
  ct: string; // base64 ciphertext
  exp: number; // epoch ms expiry
}

// ---------------------------------------------------------------------------
// Secure backend (key material + envelopes)
// ---------------------------------------------------------------------------

async function writeSecure(key: string, value: string): Promise<void> {
  if (isNative()) {
    // TODO (build-host hardening): swap @capacitor/preferences for a Keychain/Keystore plugin so the
    // AES key + envelopes are hardware-encrypted at rest.
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key, value });
    return;
  }
  if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
}

async function readSecure(key: string): Promise<string | null> {
  if (isNative()) {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key });
    return value ?? null;
  }
  return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
}

async function removeSecure(key: string): Promise<void> {
  if (isNative()) {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key });
    return;
  }
  if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
}

async function listKeys(): Promise<string[]> {
  if (isNative()) {
    const { Preferences } = await import('@capacitor/preferences');
    const { keys } = await Preferences.keys();
    return keys;
  }
  if (typeof localStorage === 'undefined') return [];
  const out: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k) out.push(k);
  }
  return out;
}

// ---------------------------------------------------------------------------
// base64 helpers (binary-safe)
// ---------------------------------------------------------------------------
//
// EVERY VALUE HANDED TO SubtleCrypto LEAVES HERE AS A TYPED-ARRAY VIEW, NEVER AS A BARE
// ArrayBuffer. This is load-bearing, not a style choice.
//
// WebIDL's BufferSource accepts both, but the two are not checked the same way. A view is
// recognised by ArrayBuffer.isView, an engine-level predicate that holds for a view created
// anywhere. A bare ArrayBuffer is recognised by a prototype comparison against the crypto
// implementation's OWN ArrayBuffer.prototype, so a buffer allocated against a different copy of
// the intrinsics is rejected with "is not instance of ArrayBuffer, Buffer, TypedArray, or
// DataView", even though it is a perfectly good ArrayBuffer. Node 20 does exactly that
// (lib/internal/crypto/webidl.js: isNonSharedArrayBuffer is ObjectPrototypeIsPrototypeOf against
// ArrayBufferPrototype); Node 22 and later replaced it with the engine-level isArrayBuffer, which
// is why the same bytes round-tripped on one runtime and not the other.
//
// That rejection landed inside getConfidential's catch, which reported the value as ABSENT and
// deleted it. A queued audio capture therefore disappeared while the UI said it had been saved.
// Passing views removes the whole class: it never depends on which copy of ArrayBuffer allocated
// the memory.

/** base64 of a buffer or any view over one. Views are read at their own offset and length. */
function bufToB64(buf: ArrayBuffer | ArrayBufferView): string {
  const bytes = ArrayBuffer.isView(buf)
    ? new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength)
    : new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Decoded bytes as a VIEW. Callers pass this straight to SubtleCrypto; never pass `.buffer`. */
function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// ---------------------------------------------------------------------------
// AES key (lazy generate + persist + reuse)
// ---------------------------------------------------------------------------

let cachedKey: CryptoKey | null = null;

function subtle(): SubtleCrypto {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (!c?.subtle) {
    throw new Error('localConfidentialStore: Web Crypto SubtleCrypto unavailable in this context.');
  }
  return c.subtle;
}

async function getKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;

  const existing = await readSecure(KEY_NAME);
  if (existing) {
    cachedKey = await subtle().importKey(
      'raw',
      b64ToBytes(existing),
      { name: 'AES-GCM' },
      false, // not extractable once re-imported
      ['encrypt', 'decrypt'],
    );
    return cachedKey;
  }

  // First run on this device: generate, persist (raw), then re-import as non-extractable.
  const generated = await subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
  const raw = new Uint8Array(await subtle().exportKey('raw', generated));
  await writeSecure(KEY_NAME, bufToB64(raw));
  cachedKey = await subtle().importKey('raw', raw, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
  return cachedKey;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const nsKey = (key: string) => `${NS}${key}`;

/** Encrypt `value` and persist it under `key` with a `ttlMs` lifetime. */
export async function setConfidential(key: string, value: string, ttlMs: number): Promise<void> {
  const cryptoKey = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await subtle().encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    new TextEncoder().encode(value),
  );

  // Clamp absurd ttls so exp stays a finite epoch (Number.MAX_SAFE_INTEGER means "effectively never").
  const exp =
    ttlMs >= Number.MAX_SAFE_INTEGER - Date.now()
      ? Number.MAX_SAFE_INTEGER
      : Date.now() + Math.max(0, ttlMs);

  const envelope: Envelope = {
    iv: bufToB64(iv),
    ct: bufToB64(ct),
    exp,
  };
  await writeSecure(nsKey(key), JSON.stringify(envelope));
}

/** Decrypt + return the value for `key`, or null if missing / expired / undecryptable. */
export async function getConfidential(key: string): Promise<string | null> {
  const raw = await readSecure(nsKey(key));
  if (!raw) return null;

  let envelope: Envelope;
  try {
    envelope = JSON.parse(raw) as Envelope;
  } catch {
    return null;
  }

  if (typeof envelope.exp === 'number' && Date.now() > envelope.exp) {
    // Expired — clean it up and report absent.
    await removeSecure(nsKey(key));
    return null;
  }

  try {
    const cryptoKey = await getKey();
    const plain = await subtle().decrypt(
      { name: 'AES-GCM', iv: b64ToBytes(envelope.iv) },
      cryptoKey,
      b64ToBytes(envelope.ct),
    );
    return new TextDecoder().decode(plain);
  } catch (err) {
    // DELETE ONLY ON A GENUINE DECRYPTION FAILURE.
    //
    // AES-GCM raises OperationError when the ciphertext does not authenticate, which is the
    // rotated-key / tampered-envelope case this branch was written for: the bytes are unreadable
    // for good and keeping them buys nothing. Anything else (a bad argument, a SubtleCrypto that
    // is not there, a host that rejects the call) says nothing about the stored bytes, and
    // deleting them turns a recoverable environment fault into permanent loss. The offline
    // capture queue keeps a client's recording behind this call, so a wrong delete here is a
    // recording gone.
    const authFailure = err instanceof Error && err.name === 'OperationError';
    if (authFailure) await removeSecure(nsKey(key));
    return null;
  }
}

/** Remove a single confidential value. */
export async function removeConfidential(key: string): Promise<void> {
  await removeSecure(nsKey(key));
}

/**
 * Wipe ALL namespaced confidential data (every value + the AES key). Used on logout / failed
 * biometric / remote wipe. After this, getConfidential returns null for everything and a fresh AES
 * key is generated on the next setConfidential.
 */
export async function wipeLocalConfidential(): Promise<void> {
  const keys = await listKeys();
  await Promise.all(keys.filter((k) => k.startsWith(NS)).map((k) => removeSecure(k)));
  cachedKey = null; // force key regeneration after a wipe
}
